// Таблицата на „Продадени продукти" — теоретично срещу реално: # · Продукт ·
// [По рецепта] К-во · Бруто · [Разнесено] К-во · Бруто · [Разлика] К-во ·
// Бруто, с ред „Общо за периода" (само парите — количествата са в различни
// мерни единици). Групите са двуредово заглавие с подсказка какво значи всяка.
// Заглавията сортират; „Бруто" по рецепта има филтър от–до. Шевронът „›"
// казва, че редът се отваря: ↑/↓ по редовете, Enter/клик отваря складовата
// карта на продукта. На телефон — карти.
// Средната цена и нетото са в Excel (таблицата щеше да стане 10 колони).
import type { ReactNode } from "react";
import { Tooltip } from "antd";
import { useTranslation } from "react-i18next";
import { formatMoney } from "../../components/numbers";
import { formatQty } from "../../components/qty";
import { sectionList, type ColumnSections } from "../../components/filters/useColumnFilters";
import { OpenChevron, ReportHead } from "../components/ReportListBits";
import {
  REPORT_HEAD_CLASS,
  REPORT_HEADER_ROW_CLASS,
  REPORT_OPENABLE_ROW_CLASS,
  REPORT_TOTAL_ROW_CLASS,
  moveRowFocus,
  openableRowProps,
} from "../components/reportListModel";
import type { SoldFilterKey, SoldSort, SoldSortKey } from "../components/soldReportModel";
import { SignedMoney, SignedQuantity, SubstitutionMark } from "./SoldVarianceBits";
import type { SoldRow, SoldVarianceTotals } from "./soldVarianceModel";

// Литерален клас — Tailwind сканира само пълни низове
const GRID_CLASS =
  "grid grid-cols-[40px_minmax(180px,1fr)_118px_100px_118px_100px_118px_100px_12px] items-center gap-x-2.5 px-[18px]";
// Лява черта пред всяка група — трите двойки колони се четат като три блока
const GROUP_START_CLASS = "border-l border-shade-2 pl-2.5";

type Props = {
  rows: SoldRow[];
  isCards: boolean;
  decimalsOf: (productId: number) => number;
  sort: SoldSort | null;
  onSort: (key: SoldSortKey) => void;
  filters: ColumnSections<SoldFilterKey>;
  onOpen: (row: SoldRow) => void;
  totals: SoldVarianceTotals;
  footer?: ReactNode;
};

const Quantity = ({ value, decimals, unit }: { value: number | null; decimals: number; unit: string | null }) =>
  value == null ? (
    <span className="text-faint">…</span>
  ) : (
    <span className="whitespace-nowrap font-sheet tabular-nums text-ink">
      {formatQty(value, decimals)}
      {unit ? <span className="text-muted"> {unit}</span> : null}
    </span>
  );

export const SoldProductsTable = ({ rows, isCards, decimalsOf, sort, onSort, filters, onOpen, totals, footer }: Props) => {
  const { t } = useTranslation();
  const money = (value: number | null | undefined, className = "text-ink-2") =>
    value == null ? (
      <span className="text-faint">…</span>
    ) : (
      <span className={`font-sheet tabular-nums ${className}`}>{formatMoney(value)}</span>
    );

  if (isCards) {
    return (
      <div onKeyDown={moveRowFocus}>
        {rows.map((row) => {
          const decimals = decimalsOf(row.sellableId);
          return (
            <div
              key={row.sellableId}
              data-sellable-id={row.sellableId}
              {...openableRowProps(() => onOpen(row))}
              className="flex cursor-pointer items-center gap-2.5 border-t border-shade-1 px-3.5 py-3 outline-none focus-visible:bg-fill"
            >
              <div className="flex min-w-0 flex-1 flex-col gap-[5px]">
                <div className="flex items-baseline gap-2">
                  <span className="min-w-0 flex-1 truncate font-semibold text-ink">{row.name}</span>
                  <SubstitutionMark actual={row.actual} decimals={decimals} unit={row.unit} />
                  <span className="font-bold">
                    <Quantity value={row.quantity} decimals={decimals} unit={row.unit} />
                  </span>
                </div>
                <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
                  <span className="min-w-0 truncate">
                    {t("inventoryPage.sdGroupPosted")}{" "}
                    <Quantity value={row.actual?.quantity ?? null} decimals={decimals} unit={row.unit} />
                  </span>
                  <span className="font-sheet text-sm font-semibold text-ink">{formatMoney(row.totalSum)}</span>
                </div>
                {row.variance && (
                  <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
                    <span>
                      {t("inventoryPage.sdGroupDiff")}{" "}
                      <SignedQuantity value={row.variance.quantity} decimals={decimals} unit={row.unit} />
                    </span>
                    <SignedMoney value={row.variance.valueWithVat} />
                  </div>
                )}
              </div>
              <OpenChevron />
            </div>
          );
        })}
        {footer}
        <div className="flex flex-col gap-1 border-t border-shade-2 bg-fill px-3.5 py-3">
          <div className="flex items-baseline justify-between gap-2.5">
            <span className={REPORT_HEAD_CLASS}>{t("inventoryPage.periodTotal")}</span>
            <span className="font-sheet font-bold text-brand-deep-ink">{formatMoney(totals.theoreticalGross)}</span>
          </div>
          <div className="flex items-baseline justify-between gap-2.5 text-[12.5px] text-body">
            <span>
              {t("inventoryPage.sdGroupPosted")} {money(totals.actualLoaded ? totals.actualGross : null)}
            </span>
            <SignedMoney value={totals.actualLoaded ? totals.varianceGross : null} strong />
          </div>
        </div>
      </div>
    );
  }

  const head = (
    label: string,
    options: { sortKey?: SoldSortKey; align?: "right"; filters?: ReturnType<typeof sectionList> } = {},
  ) => <ReportHead label={label} sort={sort} onSort={onSort} {...options} />;
  const group = (label: string, tip: string) => (
    <Tooltip title={<span className="text-[12.5px] leading-[1.4]">{tip}</span>}>
      <span
        tabIndex={0}
        aria-label={`${label}. ${tip}`}
        className={`col-span-2 ${GROUP_START_CLASS} cursor-help text-center outline-none focus-visible:underline ${REPORT_HEAD_CLASS}`}
      >
        {label}
      </span>
    </Tooltip>
  );

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[1002px]" onKeyDown={moveRowFocus}>
        <div className={`${GRID_CLASS} pt-2`}>
          <span className="col-span-2" />
          {group(t("inventoryPage.sdGroupRecipe"), t("inventoryPage.sdGroupRecipeTip"))}
          {group(t("inventoryPage.sdGroupPosted"), t("inventoryPage.sdGroupPostedTip"))}
          {group(t("inventoryPage.sdGroupDiff"), t("inventoryPage.sdGroupDiffTip"))}
        </div>
        <div className={`${GRID_CLASS} ${REPORT_HEADER_ROW_CLASS}`}>
          {head("#")}
          {head(t("inventoryPage.colProductName"), { sortKey: "name" })}
          <div className={`${GROUP_START_CLASS} flex justify-end`}>
            {head(t("inventoryPage.sdColQty"), { sortKey: "qty", align: "right" })}
          </div>
          {head(t("inventoryPage.colGross"), { sortKey: "gross", align: "right", filters: sectionList(filters.gross) })}
          <div className={`${GROUP_START_CLASS} flex justify-end`}>
            {head(t("inventoryPage.sdColQty"), { sortKey: "actualQty", align: "right" })}
          </div>
          {head(t("inventoryPage.colGross"), { sortKey: "actualGross", align: "right" })}
          <div className={`${GROUP_START_CLASS} flex justify-end`}>
            {head(t("inventoryPage.sdColQty"), { sortKey: "diffQty", align: "right" })}
          </div>
          {head(t("inventoryPage.colGross"), { sortKey: "diffGross", align: "right" })}
          <span />
        </div>
        {rows.map((row, index) => {
          const decimals = decimalsOf(row.sellableId);
          return (
            <div
              key={row.sellableId}
              data-sellable-id={row.sellableId}
              {...openableRowProps(() => onOpen(row))}
              className={`${GRID_CLASS} ${REPORT_OPENABLE_ROW_CLASS}`}
            >
              <span className="font-sheet text-faint">{index + 1}</span>
              <span className="flex min-w-0 items-center gap-1.5">
                <span className="truncate font-semibold text-ink">{row.name}</span>
                <SubstitutionMark actual={row.actual} decimals={decimals} unit={row.unit} />
              </span>
              <span className={`${GROUP_START_CLASS} text-right`}>
                <Quantity value={row.quantity} decimals={decimals} unit={row.unit} />
              </span>
              <span className="text-right">{money(row.totalSum, "font-semibold text-ink")}</span>
              <span className={`${GROUP_START_CLASS} text-right`}>
                <Quantity value={row.actual?.quantity ?? null} decimals={decimals} unit={row.unit} />
              </span>
              <span className="text-right">{money(row.actual?.valueWithVat ?? null)}</span>
              <span className={`${GROUP_START_CLASS} text-right`}>
                <SignedQuantity value={row.variance?.quantity ?? null} decimals={decimals} />
              </span>
              <span className="text-right">
                <SignedMoney value={row.variance?.valueWithVat ?? null} />
              </span>
              <OpenChevron />
            </div>
          );
        })}
        {footer}
        {rows.length > 0 && (
          <div className={`${GRID_CLASS} ${REPORT_TOTAL_ROW_CLASS}`}>
            <span className={`${REPORT_HEAD_CLASS} col-span-3`}>{t("inventoryPage.periodTotal")}</span>
            <span className="text-right">{money(totals.theoreticalGross, "font-bold text-brand-deep-ink")}</span>
            <span className={GROUP_START_CLASS} />
            <span className="text-right">
              {money(totals.actualLoaded ? totals.actualGross : null, "font-semibold text-ink")}
            </span>
            <span className={GROUP_START_CLASS} />
            <span className="text-right">
              <SignedMoney value={totals.actualLoaded ? totals.varianceGross : null} strong />
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SoldProductsTable;
